import { useMemo } from 'react';

import { fromPromise } from 'xstate5';

import { Allergen, DietaryRestriction } from '@sg/graphql-schema';
import { type DietaryPreferencesMachineImplementations } from '@sg/shared-fe/features/menu';

import {
  useUpdateDietaryPreferencesMutation,
  useUpdateDietaryPreferencesWithAllergensMutation,
} from './graphql/CustomerDietaryPreferences.generated';
import { getDietaryPreferencesList } from './helpers';

// ─────────────────────────────────────────────────────────────────────────────

export const useUpdateDietaryPreferencesActor = () => {
  const [, updatePreferencesWithRestrictions] = useUpdateDietaryPreferencesMutation();
  const [, updatePreferencesWithAllergens] = useUpdateDietaryPreferencesWithAllergensMutation();

  return useMemo<UpdateCustomerDietaryPreferences>(
    () =>
      fromPromise(async (args) => {
        const { input } = args;
        const { pendingPreferences, isAllergensEnabled } = input;

        const { error } = isAllergensEnabled
          ? await updatePreferencesWithAllergens({
              input: {
                allergens: getDietaryPreferencesList(pendingPreferences) as Allergen[],
              },
            })
          : await updatePreferencesWithRestrictions({
              input: {
                dietaryRestrictions: getDietaryPreferencesList(
                  pendingPreferences,
                ) as DietaryRestriction[],
              },
            });

        if (error) {
          throw new Error('Failed to update customer dietary preferences');
        }
      }),
    [updatePreferencesWithAllergens, updatePreferencesWithRestrictions],
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UpdateCustomerDietaryPreferences = NonNullable<
  NonNullable<DietaryPreferencesMachineImplementations['actors']>['updateRemotePreferences']
>;
